import { useState } from "react";
import { Check, ChevronLeft, ChevronRight, Phone, Calendar as CalendarIcon, Car, DollarSign, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

const LEAD_SOURCES = [
  "Instagram",
  "Facebook",
  "TikTok",
  "YouTube",
  "Google",
  "Word of Mouth",
  "Recommended by a Friend",
  "Other"
];

// Interest types
const INTEREST_TYPES = [
  { value: "test_drive", label: "Test Drive", icon: Car },
  { value: "consultation", label: "Consultation Call", icon: Phone },
  { value: "pricing_info", label: "Pricing Information", icon: DollarSign }
];

// Vehicle models
const VEHICLE_MODELS = [
  { value: "HR-V", label: "HR-V" },
  { value: "CR-V", label: "CR-V" },
  { value: "Pilot", label: "Pilot" },
  { value: "Elevate", label: "Elevate" },
  { value: "not_sure", label: "Not sure yet" }
];

// Purchase timeline options
const PURCHASE_TIMELINE = [
  { value: "immediately", label: "Immediately" },
  { value: "within_1_month", label: "Within 1 month" },
  { value: "1_3_months", label: "1–3 months" },
  { value: "just_exploring", label: "Just exploring" }
];

// Payment method options
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_financing", label: "Through bank financing" },
  { value: "not_sure", label: "Not sure yet" }
];

// Monthly budget ranges (JMD)
const MONTHLY_BUDGET_RANGES = [
  { value: "under_60k", label: "Under J$60,000", min: 0, max: 59999 },
  { value: "60k_70k", label: "J$60,000 - J$70,000", min: 60000, max: 70000, matchesModel: "Elevate" },
  { value: "70k_80k", label: "J$70,000 - J$80,000", min: 70001, max: 79999 },
  { value: "80k_95k", label: "J$80,000 - J$95,000", min: 80000, max: 95000, matchesModel: "HR-V" },
  { value: "95k_105k", label: "J$95,000 - J$105,000", min: 95001, max: 104999 },
  { value: "105k_125k", label: "J$105,000 - J$125,000", min: 105000, max: 125000, matchesModel: "CR-V" },
  { value: "above_125k", label: "Above J$125,000", min: 125001, max: Infinity },
  { value: "other", label: "Other / Not sure", min: null, max: null }
];

// Trade-in options
const TRADE_IN_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" }
];

// Get model from budget range
const getMatchedModelFromBudget = (budgetValue) => {
  const budget = MONTHLY_BUDGET_RANGES.find(b => b.value === budgetValue);
  return budget?.matchesModel || null;
};

// Check if budget matches selected model
const doesBudgetMatchModel = (budgetValue, selectedModel) => {
  const budget = MONTHLY_BUDGET_RANGES.find(b => b.value === budgetValue);
  if (!budget || !budget.matchesModel) return false;
  return budget.matchesModel.toLowerCase() === selectedModel?.toLowerCase();
};

// Get more affordable matched model
const getMoreAffordableMatch = (budgetValue, selectedModel) => {
  const matchedModel = getMatchedModelFromBudget(budgetValue);
  if (!matchedModel) return null;
  
  // Model affordability hierarchy: Elevate < HR-V < CR-V < Pilot
  const hierarchy = ["Elevate", "HR-V", "CR-V", "Pilot"];
  const selectedIndex = hierarchy.indexOf(selectedModel);
  const matchedIndex = hierarchy.indexOf(matchedModel);
  
  if (matchedIndex < selectedIndex && matchedIndex >= 0) {
    return matchedModel;
  }
  return null;
};

// Calculate lead classification
export const calculateLeadClassification = (formData) => {
  const { payment_method, monthly_budget, purchase_timeline, model_interested } = formData;
  
  // Rule 1: If financing is "Not sure yet" -> LCO
  if (payment_method === "not_sure") {
    return { classification: "LCO", reason: "Payment method undecided" };
  }
  
  // Rule 2: Check budget match
  const budgetMatchesSelected = doesBudgetMatchModel(monthly_budget, model_interested);
  const affordableMatch = getMoreAffordableMatch(monthly_budget, model_interested);
  const matchedModelFromBudget = getMatchedModelFromBudget(monthly_budget);
  
  // Rule 3: If selected model is "not_sure", default to LCO unless strong signals
  if (model_interested === "not_sure") {
    if (matchedModelFromBudget && 
        (purchase_timeline === "immediately" || purchase_timeline === "within_1_month") &&
        (payment_method === "cash" || payment_method === "bank_financing")) {
      return { 
        classification: "MCO", 
        reason: "Model undecided but budget and timeline favorable",
        matchedModel: matchedModelFromBudget
      };
    }
    return { classification: "LCO", reason: "Model selection undecided" };
  }
  
  // Rule 4: Budget matches selected vehicle
  if (budgetMatchesSelected) {
    // HCO: Immediately or Within 1 month + Cash/Bank financing
    if ((purchase_timeline === "immediately" || purchase_timeline === "within_1_month") &&
        (payment_method === "cash" || payment_method === "bank_financing")) {
      return { classification: "HCO", reason: "Budget matches, ready to buy soon" };
    }
    
    // MCO: 1-3 months + Cash/Bank financing
    if (purchase_timeline === "1_3_months" &&
        (payment_method === "cash" || payment_method === "bank_financing")) {
      return { classification: "MCO", reason: "Budget matches, medium-term timeline" };
    }
    
    // LCO: Just exploring
    if (purchase_timeline === "just_exploring") {
      return { classification: "LCO", reason: "Just exploring options" };
    }
  }
  
  // Rule 5: Budget doesn't match but matches a more affordable model -> MCO
  if (affordableMatch) {
    return { 
      classification: "MCO", 
      reason: `Budget matches ${affordableMatch} instead of ${model_interested}`,
      matchedModel: affordableMatch
    };
  }
  
  // Rule 6: Budget doesn't match any model -> LCO
  if (!matchedModelFromBudget && monthly_budget !== "above_125k") {
    return { classification: "LCO", reason: "Budget does not match available models" };
  }
  
  // Rule 7: Above 125k budget - check timeline
  if (monthly_budget === "above_125k") {
    if ((purchase_timeline === "immediately" || purchase_timeline === "within_1_month") &&
        (payment_method === "cash" || payment_method === "bank_financing")) {
      return { classification: "HCO", reason: "High budget, ready to buy" };
    }
    if (purchase_timeline === "1_3_months") {
      return { classification: "MCO", reason: "High budget, medium-term timeline" };
    }
  }
  
  // Default fallback -> LCO
  return { classification: "LCO", reason: "Default classification" };
};

const QualificationForm = ({ vehicles = [], context = "landing" }) => {
  // Total steps: 1=Contact, 2=Interest, 3=Model, 4=Timeline, 5=Payment, 6=Budget, 7=TradeIn, 8=DateTime (if needed)
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    vehicle_interested: "",
    lead_source: "",
    lead_source_other: "",
    notes: "",
    // Qualification fields
    interest_type: "",
    model_interested: "",
    purchase_timeline: "",
    payment_method: "",
    monthly_budget: "",
    has_trade_in: "",
    // Booking fields
    booking_type: "",
    preferred_date: "",
    preferred_time: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Only Test Drive requires a date/time step
  const needsDateStep = formData.interest_type === "test_drive";
  const totalSteps = needsDateStep ? 8 : 7;

  const handleNext = () => {
    // Validation for each step
    if (step === 1) {
      const missing = [];
      if (!formData.full_name?.trim()) missing.push("Full Name");
      if (!formData.email?.trim()) missing.push("Email Address");
      if (!formData.phone?.trim()) missing.push("Phone Number");
      if (!formData.lead_source) missing.push("How Did You Find Us?");
      if (missing.length) {
        toast.error(`Please fill in: ${missing.join(", ")}`);
        return;
      }
    }
    if (step === 2 && !formData.interest_type) {
      toast.error("Please select what you're interested in");
      return;
    }
    if (step === 3 && !formData.model_interested) {
      toast.error("Please select a model");
      return;
    }
    if (step === 4 && !formData.purchase_timeline) {
      toast.error("Please select your purchase timeline");
      return;
    }
    if (step === 5 && !formData.payment_method) {
      toast.error("Please select a payment method");
      return;
    }
    if (step === 6 && !formData.monthly_budget) {
      toast.error("Please select your monthly budget");
      return;
    }
    if (step === 7 && !formData.has_trade_in) {
      toast.error("Please indicate if you have a trade-in");
      return;
    }
    
    // If no date step needed (consultation call or pricing info), submit after trade-in
    if (step === 7 && !needsDateStep) {
      handleSubmit();
      return;
    }
    
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (needsDateStep && (!formData.preferred_date || !formData.preferred_time)) {
      toast.error("Please select a date and time");
      return;
    }

    setLoading(true);
    try {
      // Calculate classification
      const { classification, reason, matchedModel } = calculateLeadClassification(formData);
      
      // Map interest_type to booking_type
      const bookingType = formData.interest_type === "test_drive"
        ? "test_drive"
        : "consultation_call";
      
      // Get vehicle name from model
      const vehicleName = formData.model_interested === "not_sure" 
        ? "Not sure yet"
        : vehicles.find(v => v.name.includes(formData.model_interested))?.name || formData.model_interested;

      await axios.post(`${API}/leads`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        vehicle_interested: vehicleName,
        lead_source: formData.lead_source,
        lead_source_other: formData.lead_source_other,
        notes: formData.notes,
        booking_type: bookingType,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        // Qualification data
        interest_type: formData.interest_type,
        model_interested: formData.model_interested,
        purchase_timeline: formData.purchase_timeline,
        payment_method: formData.payment_method,
        monthly_budget: formData.monthly_budget,
        has_trade_in: formData.has_trade_in,
        matched_affordable_model: matchedModel || null,
        lead_classification: classification,
        classification_reason: reason
      });
      
      setSubmitted(true);
      toast.success("Thank you! We'll contact you shortly.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getTimeSlots = () => {
    return [
      "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
      "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
      "3:00 PM", "3:30 PM", "4:00 PM"
    ];
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const resetForm = () => {
    setStep(1);
    setSubmitted(false);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      vehicle_interested: "",
      lead_source: "",
      lead_source_other: "",
      notes: "",
      interest_type: "",
      model_interested: "",
      purchase_timeline: "",
      payment_method: "",
      monthly_budget: "",
      has_trade_in: "",
      booking_type: "",
      preferred_date: "",
      preferred_time: ""
    });
  };

  const getStepTitle = () => {
    switch(step) {
      case 1: return "Your Contact Details";
      case 2: return "What are you interested in?";
      case 3: return "Which model are you considering?";
      case 4: return "When are you looking to purchase?";
      case 5: return "How do you plan to purchase?";
      case 6: return "What is your monthly budget?";
      case 7: return "Do you have a trade-in?";
      case 8: return "Select Date & Time";
      default: return "Book Your Visit";
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-[#E5E7EB]/50 text-center">
        <div className="w-20 h-20 rounded-full bg-[#388E3C]/10 flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-[#388E3C]" />
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
          Thank You!
        </h2>
        <p className="text-lg text-[#4A4A4A] mb-2">
          Your request has been submitted!
        </p>
        <p className="text-[#6B5E55] mb-8">
          We'll contact you within 24 hours to discuss your {formData.model_interested !== "not_sure" ? formData.model_interested : "Honda"} inquiry.
        </p>
        <Button 
          onClick={resetForm} 
          className="btn-secondary"
          data-testid="book-another-btn"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-[#E5E7EB]/50">
      <div className="text-center mb-8">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-2">
          {getStepTitle()}
        </h2>
        
        {/* Progress bar */}
        <div className="flex items-center justify-center gap-1 mt-4">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div 
              key={i} 
              className={`h-1.5 rounded-full transition-all ${
                i + 1 <= step ? 'bg-[#E91E8C] w-8' : 'bg-gray-200 w-4'
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-[#6B5E55] mt-2">Step {step} of {totalSteps}</p>
      </div>

      {/* Step 1: Contact Information */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <Label className="form-label">Full Name *</Label>
              <Input
                type="text"
                placeholder="John Smith"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="form-input"
                data-testid="input-full-name"
              />
            </div>
            
            <div className="form-group">
              <Label className="form-label">Email Address *</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                data-testid="input-email"
              />
            </div>
            
            <div className="form-group">
              <Label className="form-label">Phone Number *</Label>
              <Input
                type="tel"
                placeholder="+1 876 555 1234"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="form-input"
                data-testid="input-phone"
              />
            </div>
            
            <div className="form-group">
              <Label className="form-label">How Did You Find Us? *</Label>
              <Select
                value={formData.lead_source}
                onValueChange={(value) => setFormData({ ...formData, lead_source: value })}
              >
                <SelectTrigger className="form-input" data-testid="select-source">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {formData.lead_source === "Other" && (
            <div className="form-group">
              <Label className="form-label">Please specify</Label>
              <Input
                type="text"
                placeholder="How did you hear about us?"
                value={formData.lead_source_other}
                onChange={(e) => setFormData({ ...formData, lead_source_other: e.target.value })}
                className="form-input"
                data-testid="input-source-other"
              />
            </div>
          )}
          
          <div className="form-group">
            <Label className="form-label">Additional Notes (Optional)</Label>
            <Textarea
              placeholder="Any specific requirements or questions?"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="form-textarea min-h-[80px]"
              data-testid="input-notes"
            />
          </div>
        </div>
      )}

      {/* Step 2: Interest Type */}
      {step === 2 && (
        <div className="space-y-4">
          {INTEREST_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setFormData({ ...formData, interest_type: type.value })}
                className={`w-full p-5 rounded-xl border-2 transition-all text-left ${
                  formData.interest_type === type.value
                    ? 'border-[#E91E8C] bg-[#FFF5F9]'
                    : 'border-[#E5E7EB] hover:border-[#E91E8C]/50'
                }`}
                data-testid={`interest-${type.value}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    formData.interest_type === type.value ? 'bg-[#E91E8C]' : 'bg-[#E91E8C]/10'
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      formData.interest_type === type.value ? 'text-white' : 'text-[#E91E8C]'
                    }`} />
                  </div>
                  <span className="font-medium text-lg text-[#1A1A1A]">{type.label}</span>
                  {formData.interest_type === type.value && (
                    <Check className="w-5 h-5 text-[#E91E8C] ml-auto" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Step 3: Model Selection */}
      {step === 3 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {VEHICLE_MODELS.map((model) => (
            <button
              key={model.value}
              onClick={() => setFormData({ ...formData, model_interested: model.value })}
              className={`p-4 rounded-xl border-2 transition-all ${
                formData.model_interested === model.value
                  ? 'border-[#E91E8C] bg-[#FFF5F9]'
                  : 'border-[#E5E7EB] hover:border-[#E91E8C]/50'
              }`}
              data-testid={`model-${model.value}`}
            >
              <div className="text-center">
                <Car className={`w-8 h-8 mx-auto mb-2 ${
                  formData.model_interested === model.value ? 'text-[#E91E8C]' : 'text-[#6B5E55]'
                }`} />
                <span className="font-medium text-[#1A1A1A]">{model.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Step 4: Purchase Timeline */}
      {step === 4 && (
        <div className="space-y-3">
          {PURCHASE_TIMELINE.map((option) => (
            <button
              key={option.value}
              onClick={() => setFormData({ ...formData, purchase_timeline: option.value })}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                formData.purchase_timeline === option.value
                  ? 'border-[#E91E8C] bg-[#FFF5F9]'
                  : 'border-[#E5E7EB] hover:border-[#E91E8C]/50'
              }`}
              data-testid={`timeline-${option.value}`}
            >
              <div className="flex items-center gap-3">
                <Clock className={`w-5 h-5 ${
                  formData.purchase_timeline === option.value ? 'text-[#E91E8C]' : 'text-[#6B5E55]'
                }`} />
                <span className="font-medium text-[#1A1A1A]">{option.label}</span>
              </div>
              {formData.purchase_timeline === option.value && (
                <Check className="w-5 h-5 text-[#E91E8C]" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Step 5: Payment Method */}
      {step === 5 && (
        <div className="space-y-3">
          {PAYMENT_METHODS.map((method) => (
            <button
              key={method.value}
              onClick={() => setFormData({ ...formData, payment_method: method.value })}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                formData.payment_method === method.value
                  ? 'border-[#E91E8C] bg-[#FFF5F9]'
                  : 'border-[#E5E7EB] hover:border-[#E91E8C]/50'
              }`}
              data-testid={`payment-${method.value}`}
            >
              <div className="flex items-center gap-3">
                <DollarSign className={`w-5 h-5 ${
                  formData.payment_method === method.value ? 'text-[#E91E8C]' : 'text-[#6B5E55]'
                }`} />
                <span className="font-medium text-[#1A1A1A]">{method.label}</span>
              </div>
              {formData.payment_method === method.value && (
                <Check className="w-5 h-5 text-[#E91E8C]" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Step 6: Monthly Budget */}
      {step === 6 && (
        <div className="space-y-3">
          <p className="text-sm text-[#6B5E55] mb-4 text-center">
            Select your comfortable monthly payment range
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {MONTHLY_BUDGET_RANGES.map((budget) => (
              <button
                key={budget.value}
                onClick={() => setFormData({ ...formData, monthly_budget: budget.value })}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  formData.monthly_budget === budget.value
                    ? 'border-[#E91E8C] bg-[#FFF5F9]'
                    : 'border-[#E5E7EB] hover:border-[#E91E8C]/50'
                }`}
                data-testid={`budget-${budget.value}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#1A1A1A]">{budget.label}</span>
                  {formData.monthly_budget === budget.value && (
                    <Check className="w-5 h-5 text-[#E91E8C]" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 7: Trade-in */}
      {step === 7 && (
        <div className="space-y-4">
          <p className="text-center text-[#6B5E55] mb-4">
            Do you currently own a vehicle you'd like to trade in?
          </p>
          <div className="flex gap-4 justify-center">
            {TRADE_IN_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setFormData({ ...formData, has_trade_in: option.value })}
                className={`px-12 py-4 rounded-xl border-2 transition-all ${
                  formData.has_trade_in === option.value
                    ? 'border-[#E91E8C] bg-[#FFF5F9]'
                    : 'border-[#E5E7EB] hover:border-[#E91E8C]/50'
                }`}
                data-testid={`tradein-${option.value}`}
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className={`w-5 h-5 ${
                    formData.has_trade_in === option.value ? 'text-[#E91E8C]' : 'text-[#6B5E55]'
                  }`} />
                  <span className="font-medium text-lg text-[#1A1A1A]">{option.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 8: Date & Time Selection */}
      {step === 8 && (
        <div className="space-y-6">
          <div className="form-group">
            <Label className="form-label">Preferred Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "form-input w-full flex items-center justify-between text-left",
                    !formData.preferred_date && "text-[#6B5E55]"
                  )}
                  data-testid="select-date"
                >
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-[#E91E8C]" />
                    {formData.preferred_date
                      ? formatDateDisplay(formData.preferred_date)
                      : "Pick a weekday"}
                  </span>
                  <ChevronRight className="w-4 h-4 rotate-90 text-[#6B5E55]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={formData.preferred_date ? new Date(`${formData.preferred_date}T00:00:00`) : undefined}
                  onSelect={(date) => {
                    if (!date) return;
                    // Local YYYY-MM-DD (avoid UTC off-by-one)
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, "0");
                    const d = String(date.getDate()).padStart(2, "0");
                    setFormData({ ...formData, preferred_date: `${y}-${m}-${d}` });
                  }}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const maxDate = new Date();
                    maxDate.setDate(today.getDate() + 60);
                    const day = date.getDay();
                    // Disable past dates, weekends, and anything more than 60 days out
                    return date < today || date > maxDate || day === 0 || day === 6;
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-[#6B5E55] mt-1">Available Monday – Friday only</p>
          </div>
          
          <div className="form-group">
            <Label className="form-label">Preferred Time *</Label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {getTimeSlots().map((time) => (
                <button
                  key={time}
                  onClick={() => setFormData({ ...formData, preferred_time: time })}
                  className={`py-2 px-3 rounded-lg text-sm transition-all ${
                    formData.preferred_time === time
                      ? 'bg-[#E91E8C] text-white'
                      : 'bg-gray-100 text-[#4A4A4A] hover:bg-[#E91E8C]/10'
                  }`}
                  data-testid={`time-${time.replace(/\s/g, '-')}`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
          
          <div className="p-4 bg-[#FFF5F9] rounded-xl">
            <div className="flex items-center gap-2 text-sm text-[#6B5E55]">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#E91E8C]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>
                {formData.interest_type === "test_drive" ? "Test drive" : "Showroom visit"} at <strong>ATL Hagley Park, Kingston</strong>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4 mt-8">
        {step > 1 && (
          <Button
            onClick={handleBack}
            variant="outline"
            className="flex-1"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}
        
        <Button
          onClick={step === 8 || (step === 7 && !needsDateStep) ? handleSubmit : handleNext}
          disabled={loading}
          className="flex-1 btn-primary"
          data-testid="next-btn"
        >
          {loading ? (
            "Submitting..."
          ) : step === 8 || (step === 7 && !needsDateStep) ? (
            "Submit Request"
          ) : (
            <>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default QualificationForm;
